import React from 'react';
import Breadcrumb, { BreadcrumbSegment } from './Breadcrumb';
import MascotScene from '../brand/MascotScene';
import ChapterHeading from '../brand/ChapterHeading';
import Portrait from '../brand/Portrait';
import { BADGE_FOR_MOTION, BearriBadgeName, BearriCue } from '../brand/BearriBadge';
import type { BearriMotionName, BearriMotionAsset } from '../brand/motionAssets';
import '../../styles/workspace.css';

/** Logical workspace group. Colours the heading's tab stripe and paperclip. */
export type WorkspaceChapter = 'land' | 'run' | 'keep' | 'home';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  illustration?: string;
  motion?: BearriMotionName;
  motionAsset?: BearriMotionAsset;
  badge?: BearriBadgeName;
  busy?: boolean;
  titleId?: string;
  avatar?: { src?: string; alt: string };
  breadcrumbs: BreadcrumbSegment[];
  prependDashboard?: boolean;
  actions?: React.ReactNode;
  className?: string;
  /** A section identity, not a status or claim about the underlying data. Rendered as the cut-paper tab. */
  eyebrow?: string;
  chapter?: WorkspaceChapter;
  /** A one-shot clip in the pin's slot for an earned moment (a won deal). */
  cue?: BearriCue;
}

export default function PageHeader({title, subtitle, illustration, motion, motionAsset, badge, busy, titleId, avatar, breadcrumbs, prependDashboard = true, actions, className = '', eyebrow, chapter, cue}: PageHeaderProps) {
  // `badge` names the resting pin; `motion` is the clip the pin comes alive with while `busy`
  // (and, for pages that only pass motion, also picks the pin); `cue` is a one-shot clip.
  const badgeName = !avatar ? badge || (motion && BADGE_FOR_MOTION[motion]) : undefined;
  const hasArt = Boolean(avatar || badgeName || motionAsset || illustration);
  // A one-segment trail ("Dashboard › Outreach") says nothing the chapter tab
  // above the title does not; only a real trail (a deal inside Outreach) is kept.
  const showTrail = breadcrumbs.length > 0 && !(eyebrow && breadcrumbs.length === 1);
  return (
    <header className={`br-page-header${hasArt ? ' br-page-header--illustrated' : ''}${avatar ? ' br-page-header--portrait' : ''} ${className}`} data-chapter={chapter}>
      {showTrail && <Breadcrumb segments={breadcrumbs} prependDashboard={prependDashboard} className="mb-2" />}
      <ChapterHeading title={title} titleId={titleId} subtitle={subtitle} level={1} size="page" actions={actions} badge={badgeName} busy={busy}
        eyebrow={eyebrow} tone={chapter} motion={motion} cue={cue}
        // a person's page: their portrait stands where the bear pin would, so the name sits beside it
        art={avatar ? <Portrait name={avatar.alt} src={avatar.src} size="lg" />
          : hasArt && !badgeName ? <div className="br-page-header__stage" aria-hidden="true">
          {motionAsset ? <MascotScene asset={motionAsset} className="br-page-header__art" />
            : <img src={illustration} alt="" className="br-page-header__still" />}
        </div> : undefined} />
    </header>
  );
}
